// frontend/src/components/dashboard/pages/ShowcaseControl.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { photographerAPI } from '@services/photographerApi';
import type { ShowcaseStatus } from '@/types/showcase';

interface Props {
  eventId: string;
}

const ShowcaseControl: React.FC<Props> = ({ eventId }) => {
  const [status, setStatus] = useState<ShowcaseStatus>('none');
  const [rejectReason, setRejectReason] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await photographerAPI.getShowcaseStatus(eventId);
      setStatus(res.data?.status ?? 'none');
      setRejectReason(res.data?.rejectReason ?? null);
    } catch {
      setError('Could not load showcase status.');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await photographerAPI.submitToShowcase(eventId);
      setStatus(res.data?.status ?? 'pending');
      setRejectReason(null);
    } catch {
      setError('Could not submit to the showcase. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await photographerAPI.withdrawFromShowcase(eventId);
      setStatus(res.data?.status ?? 'none');
    } catch {
      setError('Could not withdraw the submission. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t pt-6">
      <h3 className="text-lg font-medium text-gray-900 mb-1 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-indigo-500" />
        Album Showcase
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        Submit this album to be featured on the public showcase. An admin approves each
        submission before it goes live.
      </p>

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

      {loading ? (
        <p className="text-sm text-gray-400">Loading status…</p>
      ) : status === 'none' ? (
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {busy ? 'Submitting…' : 'Submit to showcase'}
        </button>
      ) : status === 'pending' ? (
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center rounded-full bg-yellow-100 px-3 py-1 text-sm font-medium text-yellow-800">
            In review
          </span>
          <button
            type="button"
            onClick={withdraw}
            disabled={busy}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            {busy ? 'Withdrawing…' : 'Withdraw'}
          </button>
        </div>
      ) : status === 'approved' ? (
        <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800">
          Featured on the showcase
        </span>
      ) : (
        <div className="space-y-3">
          <div className="rounded-md bg-red-50 border border-red-200 p-3">
            <p className="text-sm font-medium text-red-800">Submission rejected</p>
            {rejectReason && <p className="text-sm text-red-600">{rejectReason}</p>}
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {busy ? 'Submitting…' : 'Submit to showcase'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ShowcaseControl;
